import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Loader2, CheckCircle2, AlertCircle, Package2, Barcode,
  MapPin, PackageCheck, X as XIcon, MinusCircle, PlusCircle, FastForward,
  Users, Layers,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface PedidoInfo {
  sep_id: number
  pedido_id: number
  pedido_numero: number
  cliente: string
  quantidade_prevista: number
  quantidade_separada: number
  lote_codigo: string | null
}
interface ItemConsolidado {
  produto_id: number
  produto_codigo: string
  produto_ean: string | null
  produto_descricao: string
  unidade: string
  endereco: string | null
  saida_por: string
  quantidade_prevista: number
  quantidade_separada: number
  concluido: boolean
  pedidos: PedidoInfo[]
}
interface MapaCarga {
  carga_id: number
  numero: number
  nome: string
  status: string
  total_pedidos: number
  total_previsto: number
  total_separado: number
  progresso_pct: number
  itens: ItemConsolidado[]
}

export default function OndaBipagemPage() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const qc = useQueryClient()
  const cargaId = Number(id)

  const [quantidade, setQuantidade] = useState(1)
  const inputRef = useRef<HTMLInputElement>(null)
  const [ultimoBip, setUltimoBip] = useState<{
    produto: string; msg: string; ok: boolean;
  } | null>(null)

  const { data: mapa, isLoading, error } = useQuery<MapaCarga>({
    queryKey: ['carga-mapa', cargaId],
    queryFn: async () => (await api.get(`/vendas/cargas/${cargaId}/mapa-bipagem`)).data,
  })

  useEffect(() => {
    if (mapa) inputRef.current?.focus()
  }, [mapa])

  const bipar = useMutation({
    mutationFn: async ({ codigo, qtd, forcar }: { codigo: string; qtd: number; forcar?: boolean }) =>
      (await api.post(`/vendas/cargas/${cargaId}/bipagem`, {
        codigo, quantidade: qtd, forcar_excesso: !!forcar,
      })).data,
    onSuccess: (r) => {
      const dist = r.distribuicao.map((d: any) => `#${d.pedido_id}(+${d.quantidade_adicionada})`).join(' ')
      setUltimoBip({
        produto: r.produto_descricao,
        ok: true,
        msg: `${r.produto_descricao}: ${r.total_separado}/${r.total_previsto} · ${dist}`,
      })
      qc.invalidateQueries({ queryKey: ['carga-mapa', cargaId] })
      // Beep de sucesso
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
        const osc = ctx.createOscillator(); const gain = ctx.createGain()
        osc.connect(gain); gain.connect(ctx.destination)
        osc.frequency.value = r.tudo_separado ? 1100 : 880
        gain.gain.setValueAtTime(0.1, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15)
        osc.start(); osc.stop(ctx.currentTime + 0.15)
      } catch {}
      if (r.tudo_separado) toast.success('🎉 Onda completa!', { duration: 4000 })
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.detail || 'Erro'
      setUltimoBip({ produto: '', ok: false, msg })
      if (typeof msg === 'string' && msg.includes('excede disponivel')) {
        if (confirm(`${msg}\n\nForçar bipagem mesmo assim?`)) {
          const codigo = inputRef.current?.value || ''
          if (codigo) bipar.mutate({ codigo, qtd: quantidade, forcar: true })
        }
      }
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
        const osc = ctx.createOscillator(); const gain = ctx.createGain()
        osc.connect(gain); gain.connect(ctx.destination)
        osc.frequency.value = 200
        gain.gain.setValueAtTime(0.15, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)
        osc.start(); osc.stop(ctx.currentTime + 0.3)
      } catch {}
    },
  })

  const concluir = useMutation({
    mutationFn: async () => (await api.post(`/vendas/cargas/${cargaId}/concluir-separacao`, {})).data,
    onSuccess: () => {
      toast.success('Onda concluída — pedidos em embalagem')
      qc.invalidateQueries({ queryKey: ['pedidos'] })
      nav('/vendas')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const input = inputRef.current
    if (!input || !input.value.trim()) return
    const codigo = input.value.trim()
    bipar.mutate({ codigo, qtd: quantidade })
    input.value = ''
    setQuantidade(1)
    input.focus()
  }

  if (error) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <AlertCircle className="inline mr-2" /> Erro ao carregar onda
        </div>
        <button onClick={() => nav('/vendas')} className="btn-ghost mt-3">
          <ArrowLeft size={14} /> Voltar
        </button>
      </div>
    )
  }
  if (isLoading || !mapa) {
    return (
      <div className="flex items-center justify-center h-96 text-ink-400">
        <Loader2 className="animate-spin mr-2" /> Carregando onda...
      </div>
    )
  }

  const todosConcluidos = mapa.total_separado >= mapa.total_previsto

  return (
    <div className="min-h-screen bg-ink-50">
      <div className="bg-ink-900 text-white px-6 py-3 flex items-center justify-between sticky top-0 z-40 shadow-lg">
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/vendas')} className="p-2 rounded hover:bg-ink-800">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="text-xs text-ink-400 uppercase tracking-wider">Onda consolidada</div>
            <div className="font-semibold flex items-center gap-2">
              🌊 #{mapa.numero} — {mapa.nome}
              <span className="text-xs text-ink-400 flex items-center gap-1">
                <Users size={12} /> {mapa.total_pedidos} pedidos
              </span>
            </div>
          </div>
        </div>
        <button
          disabled={!todosConcluidos || concluir.isPending}
          onClick={() => concluir.mutate()}
          className="text-sm px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 font-medium flex items-center gap-1.5">
          {concluir.isPending
            ? <><Loader2 size={14} className="animate-spin" /> Concluindo...</>
            : <><PackageCheck size={14} /> Concluir onda</>
          }
        </button>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        {/* Progresso */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-ink-600">Progresso total</div>
            <div className="text-sm">
              <span className="font-semibold">{mapa.total_separado}</span>
              <span className="text-ink-400"> / {mapa.total_previsto}</span>
            </div>
          </div>
          <div className="h-4 bg-ink-100 rounded-full overflow-hidden">
            <div className={`h-full transition-all duration-300 ${todosConcluidos ? 'bg-emerald-500' : 'bg-indigo-500'}`}
              style={{ width: `${Math.min(100, mapa.progresso_pct)}%` }} />
          </div>
        </div>

        {/* Input de bipagem */}
        {!todosConcluidos && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-ink-600">
              <Barcode size={18} />
              <label className="text-sm font-medium">Bipe o SKU ou EAN</label>
            </div>
            <div className="flex gap-2">
              <input ref={inputRef} type="text" autoComplete="off" autoFocus
                placeholder="Escaneie aqui..."
                className="flex-1 px-5 py-4 text-xl font-mono bg-ink-50 border-2 border-ink-200 rounded-lg focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-indigo-100" />
              <div className="flex items-center gap-1 bg-ink-50 rounded-lg border-2 border-ink-200 px-1">
                <button type="button" onClick={() => setQuantidade(q => Math.max(1, q - 1))}
                  className="p-2 hover:bg-ink-100 rounded">
                  <MinusCircle size={18} className="text-ink-500" />
                </button>
                <input type="number" min={1} value={quantidade}
                  onChange={e => setQuantidade(parseInt(e.target.value) || 1)}
                  className="w-16 text-center text-xl font-semibold bg-transparent outline-none" />
                <button type="button" onClick={() => setQuantidade(q => q + 1)}
                  className="p-2 hover:bg-ink-100 rounded">
                  <PlusCircle size={18} className="text-ink-500" />
                </button>
              </div>
              <button type="submit" disabled={bipar.isPending}
                className="px-6 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50">
                {bipar.isPending ? <Loader2 size={18} className="animate-spin" /> : <><FastForward size={18} className="inline" /> Bipar</>}
              </button>
            </div>
            <p className="text-xs text-ink-500">
              💡 A quantidade é distribuída FIFO entre os pedidos (mais antigo primeiro)
            </p>
          </form>
        )}

        {/* Feedback último bip */}
        {ultimoBip && (
          <div className={`rounded-xl p-3 flex items-center gap-3 text-sm ${ultimoBip.ok
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
            : 'bg-red-50 border border-red-200 text-red-900'}`}>
            {ultimoBip.ok ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            <span className="font-medium">{ultimoBip.msg}</span>
            <button onClick={() => setUltimoBip(null)} className="ml-auto p-1 hover:bg-white/50 rounded">
              <XIcon size={14} />
            </button>
          </div>
        )}

        {/* Lista de itens consolidados */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="px-4 py-3 border-b border-ink-100 text-xs font-semibold text-ink-600 uppercase tracking-wide flex items-center gap-2">
            <Layers size={14} /> Produtos agrupados ({mapa.itens.length})
          </div>
          <div className="divide-y divide-ink-100">
            {mapa.itens.map(it => (
              <div key={it.produto_id} className={`px-4 py-3 ${it.concluido ? 'bg-emerald-50/30' : ''}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${it.concluido ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'}`}>
                    {it.concluido ? <CheckCircle2 size={20} /> : <Package2 size={20} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-ink-900">{it.produto_descricao}</span>
                      <span className="text-xs text-ink-400 font-mono">#{it.produto_codigo}</span>
                      {it.produto_ean && <span className="text-xs text-ink-400 font-mono">· EAN {it.produto_ean}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-ink-500 mt-0.5">
                      {it.endereco && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={11} /> <strong>{it.endereco}</strong>
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded font-medium text-[10px] ${
                        it.saida_por === 'FEFO' ? 'bg-amber-100 text-amber-700'
                        : it.saida_por === 'MANUAL' ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                      }`}>
                        {it.saida_por}
                      </span>
                    </div>
                  </div>
                  <div className={`text-right ${it.concluido ? 'text-emerald-600' : 'text-ink-900'}`}>
                    <div className="text-2xl font-bold leading-none">
                      {it.quantidade_separada}
                      <span className="text-ink-300 text-base">/{it.quantidade_prevista}</span>
                    </div>
                    <div className="text-[10px] text-ink-400 uppercase mt-0.5">{it.unidade}</div>
                  </div>
                </div>
                {/* Pedidos que usam este produto */}
                <div className="ml-13 pl-3 border-l-2 border-ink-100 space-y-1 mt-2">
                  {it.pedidos.map(p => (
                    <div key={p.sep_id} className="flex items-center justify-between text-xs">
                      <span className="text-ink-600">
                        → Pedido <strong>#{p.pedido_numero}</strong> · {p.cliente}
                      </span>
                      <span className={`font-mono ${p.quantidade_separada >= p.quantidade_prevista ? 'text-emerald-600' : 'text-ink-500'}`}>
                        {p.quantidade_separada}/{p.quantidade_prevista}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {todosConcluidos && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5 text-center">
            <CheckCircle2 size={40} className="mx-auto text-emerald-600 mb-2" />
            <h3 className="font-semibold text-emerald-900">Onda completa! 🎉</h3>
            <p className="text-sm text-emerald-700 mt-1">
              Todos os produtos foram separados. Clique em "Concluir onda" acima pra mover os pedidos pra embalagem.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
